import AbortService from "@rmp/core/services/abortService";
import { MasterAccountAccessApplicationController } from "@rmp/core/api/masterAccountAccessApplication";
import { PageState, usePageStore } from "@rmp/core/stores/composables/page/usePageStore";
import { BaseStepState } from "@rmp/id/stores/masterAccountAccess/steps/shared/baseStepState";
import { MasterAccountApplicationStepOrdersEnum } from "@rmp/id/stores/masterAccountAccess/types/MasterAccountApplicationStepOrdersEnum";
import { defineStore } from "pinia";
import { DivisionsController } from "@rmp/core/api/divisions";
import { strictFetch } from "@rmp/core/utils/safeFetch";
import {
	ApiDivisionBranchBasin,
	ApiDivisionBranchBasinPersisted,
	ApiDivisionDepartment,
	ApiDivisionDepartmentPersisted
} from "@rmp/api-gateway";
import { useAppStore } from "@rmp/core/stores/appStore";
import { Modify } from "@rmp/core/types/Modify";
import { buildDivisionsTree, DivisionsTreeNode } from "@rmp/core/utils/helpers";

type Branch = Modify<ApiDivisionBranchBasin, {
	id: string;
}>

type Department = Modify<ApiDivisionDepartment, {
	id: string;
}>;

const abortService = new AbortService();
const masterAccountAccessApplicationController = new MasterAccountAccessApplicationController(abortService);
const divisionsController = new DivisionsController(abortService);

const page = usePageStore(abortService);

export interface MasterAccountAccessSelectBranchStepState extends BaseStepState, PageState {
	formValid: boolean;
	branches: Branch[];
	departments: Department[];
	divisionTree: DivisionsTreeNode[];
	selectedDivisionId: string;
}

const getDefaultState = (): MasterAccountAccessSelectBranchStepState => {
	return {
		...page.getDefaultPageState(),
		order: MasterAccountApplicationStepOrdersEnum.SELECT_BRANCH_STEP_ORDER,
		formValid: false,
		branches: [],
		departments: [],
		divisionTree: [],
		selectedDivisionId: ""
	}
};

export const useMasterAccountAccessSelectDivisionStepStore = defineStore({
	id: "master-account-access-select-branch-step",
	state: (): MasterAccountAccessSelectBranchStepState => getDefaultState(),
	getters: {
		...page.getters,
		stepCompleted(state: MasterAccountAccessSelectBranchStepState) {
			return !!state.selectedDivisionId && state.formValid;
		}
	},
	actions: {
		...page.actions,
		async beforeInitialized() {
			await this.fetchBranches();
		},
		async fetchBranches() {
			try {
				const { branches, departments } = await strictFetch(() => divisionsController.getDivisions());
				
				this.branches = branches.map((x) => ({
					...x.division,
					id: x.id
				}));

				this.departments = departments.map((x) => ({
					...x.division,
					id: x.id
				}));

				this.divisionTree = buildDivisionsTree({ branches, departments });
			} catch (ex) {
				useAppStore().handlePageLoadError(ex);
			}
		}
	}
});
